/**
 * How each notification type looks: icon and accent colour.
 *
 * The backend cannot send a React component, so it sends `type` and the client
 * owns the visuals. Module scope is safe here — this holds component references
 * and Tailwind class strings, and calls no `t()` (which at module scope would
 * freeze the startup language; see CLAUDE.md).
 *
 * `Record<NotificationType, …>` is deliberate: adding a type to
 * NOTIFICATION_TYPES without adding it here fails `tsc`. That is the
 * compile-time half of "easy to extend, impossible to half-extend".
 */

import {
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  CalendarSync,
  DatabaseZap,
  CalendarX,
  AlarmClock,
  CheckSquare,
  Clock,
  Handshake,
  ListChecks,
  ListX,
  Mail,
  Sparkles,
  Sun,
  Sunrise,
  Sunset,
  TrendingUp,
  UserMinus,
  UserPen,
  UserPlus,
  type LucideIcon,
} from "lucide-react";

import type { NotificationType } from "./types";

export interface NotificationPresentation {
  icon: LucideIcon;
  /** Tailwind text colour token, applied to the icon inside its bubble. */
  color: string;
}

export const NOTIFICATION_PRESENTATION: Record<NotificationType, NotificationPresentation> = {
  // Digests — the recurring rhythm. Sunrise/Sunset pair the two daily digests
  // so their order reads at a glance, and the weekly one stays calendar-shaped
  // to separate "this week" from "today".
  weekStart: { icon: CalendarClock, color: "text-info" },
  weekdayMorning: { icon: Sunrise, color: "text-info" },
  weekdayEndOfDay: { icon: Sunset, color: "text-muted-foreground" },

  // Once-ever onboarding milestones — success-coloured, because they confirm
  // something the user set up actually worked.
  calendarSyncCompleted: { icon: CalendarSync, color: "text-success" },
  firstMeeting: { icon: Sparkles, color: "text-success" },

  // AI output waiting on the user.
  followUpDrafted: { icon: Mail, color: "text-accent" },
  dealSuggestion: { icon: Handshake, color: "text-warning" },
  taskSuggestion: { icon: ListChecks, color: "text-warning" },

  // Someone else acted and this user now owns something. Info-coloured: a handoff is
  // neither a success nor a warning, it is a fact to absorb.
  assignedTask: { icon: CheckSquare, color: "text-info" },
  // UserPlus, not Handshake: Handshake is dealSuggestion, and the icon test rightly
  // refuses a duplicate — what changed here is who owns the deal, not that one exists.
  assignedDealOwner: { icon: UserPlus, color: "text-info" },

  // The four Sunday pipeline checks. Each icon names its own condition rather than
  // reusing one "deal problem" glyph — these arrive together on a Sunday morning, so a
  // shared icon would make four rows read as one repeated row.
  //
  // Close-date-in-the-past is destructive-coloured and the rest are warnings: a date that
  // has already passed is wrong now, while the other three are "worth a look".
  closeDateInPast: { icon: CalendarX, color: "text-destructive" },
  noActivity: { icon: Clock, color: "text-warning" },
  noNextSteps: { icon: ListX, color: "text-warning" },
  fewContacts: { icon: UserMinus, color: "text-warning" },
  weeklyPipelineChanges: { icon: TrendingUp, color: "text-info" },

  // Weekday morning, split out of the weekdayMorning digest. Sun rather than Sunrise so
  // the split alert is distinguishable from the digest it came out of while both exist.
  dailyBriefing: { icon: Sun, color: "text-info" },
  // AlarmClock rather than another check-box: assignedTask already owns CheckSquare, and
  // what distinguishes this one is the deadline, not the task.
  actionItemsDue: { icon: AlarmClock, color: "text-warning" },

  // Success-coloured with the onboarding milestones, because it confirms something the
  // user set up actually worked. DatabaseZap rather than CalendarSync: both are "a sync
  // finished", and the icon test rightly refuses the duplicate.
  crmSyncCompleted: { icon: DatabaseZap, color: "text-success" },

  // Job changes. The applied title change is a fact (info); the two questions are warnings —
  // they wait on someone. Building2 for the move because what changed is the company, and
  // UserPen for the title question because it is about the user's own edit.
  contactJobTitleChanged: { icon: BriefcaseBusiness, color: "text-info" },
  contactTitleChangeSuggested: { icon: UserPen, color: "text-warning" },
  contactCompanyChanged: { icon: Building2, color: "text-warning" },
};

/**
 * Presentation for a row whose `type` this build does not recognise.
 *
 * Neutral on purpose: an unknown type is shown with the server's fallback text,
 * and dressing it in a borrowed icon would imply a meaning we cannot vouch for.
 */
export const UNKNOWN_NOTIFICATION_PRESENTATION: NotificationPresentation = {
  icon: Bell,
  color: "text-muted-foreground",
};

/** Resolve presentation for a possibly-unknown type. Never returns undefined. */
export function presentationFor(type: NotificationType | null): NotificationPresentation {
  return type ? NOTIFICATION_PRESENTATION[type] : UNKNOWN_NOTIFICATION_PRESENTATION;
}
